import Layout from "@/components/feature/Layout";
import PageHeader from "@/components/base/PageHeader";
import ContactForm from "@/pages/contact/components/ContactForm";
import { usePageMeta, SITE_ORIGIN } from "@/hooks/usePageMeta";
import { siteInfo } from "@/mocks/site";

const channels = [
  {
    icon: "ri-mail-line",
    label: "一般咨询与读者来信",
    value: siteInfo.email,
    href: `mailto:${siteInfo.email}`,
    tone: "bg-primary-100 text-primary-700",
  },
  {
    icon: "ri-lightbulb-line",
    label: "调查线索投递",
    value: siteInfo.tipEmail,
    href: `mailto:${siteInfo.tipEmail}`,
    tone: "bg-accent-100 text-accent-800",
  },
];

const notes = [
  {
    icon: "ri-link",
    title: "附上原始链接",
    desc: "请提供内容的原始链接与发布时间，截图请包含账号信息与转发数据，方便我们判断传播情况。",
  },
  {
    icon: "ri-time-line",
    title: "尽量趁内容还在",
    desc: "很多内容会被删除。建议先保存截图与录屏，等材料整理好之后再统一发给我们。",
  },
  {
    icon: "ri-user-unfollow-line",
    title: "不针对具体个人",
    desc: "我们不接受匿名指控某一个具体个人的来信。涉及个人的内容，我们只引用权威渠道已公开的信息。",
  },
  {
    icon: "ri-hand-heart-line",
    title: "需要帮助也可以写信",
    desc: "如果你正在经历网暴，即使不打算投稿，也可以写信说明情况，我们会尽力给出方向性的建议。",
  },
];

export default function ContactPage() {
  usePageMeta({
    title: `联系我们｜网暴黑料线索投递与读者来信 - ${siteInfo.name}`,
    description:
      "联系黑料显微镜：读者来信、调查线索投递、内容纠错与合作申请都可以通过这里提交。我们通常会在两个工作日内查看来信，页面中也说明了哪些内容我们不会处理。",
    keywords: "联系黑料显微镜,网暴黑料线索,投稿,读者来信,内容纠错",
    path: "/contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ContactPage",
          url: `${SITE_ORIGIN}/contact`,
          name: `联系我们 - ${siteInfo.name}`,
          inLanguage: "zh-CN",
          description: "读者来信、调查线索投递与合作申请的提交页面。",
          mainEntity: {
            "@type": "Organization",
            name: siteInfo.name,
            url: SITE_ORIGIN,
            email: siteInfo.email,
            contactPoint: [
              {
                "@type": "ContactPoint",
                contactType: "读者来信",
                email: siteInfo.email,
                availableLanguage: ["zh-CN"],
              },
              {
                "@type": "ContactPoint",
                contactType: "线索投递",
                email: siteInfo.tipEmail,
                availableLanguage: ["zh-CN"],
              },
            ],
          },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
            { "@type": "ListItem", position: 2, name: "联系我们", item: `${SITE_ORIGIN}/contact` },
          ],
        },
      ],
    },
  });

  return (
    <Layout>
      <PageHeader
        crumbs={[{ label: "首页", to: "/" }, { label: "联系我们" }]}
        eyebrow="Contact"
        title="写信给我们"
        description="无论你是想提供线索、指出错误，还是只想找个人说说自己经历的事，都欢迎在这里留下信息。我们会认真读完每一封来信。"
      />

      <section className="w-full bg-background-50 py-12 md:py-16">
        <div className="max-w-content mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <div className="lg:col-span-5">
              <div className="space-y-4">
                {channels.map((item) => (
                  <div
                    key={item.label}
                    className="p-5 rounded-lg bg-background-100 border border-background-200"
                  >
                    <span className={`w-11 h-11 flex items-center justify-center rounded-md ${item.tone}`}>
                      <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
                    </span>
                    <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">
                      {item.label}
                    </h2>
                    <a
                      href={item.href}
                      className="mt-1.5 block text-sm text-foreground-600 hover:text-primary-600 transition-colors break-all"
                    >
                      {item.value}
                    </a>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-5 rounded-lg bg-secondary-50 border border-secondary-200">
                <h2 className="font-heading text-base font-bold text-secondary-950 flex items-center gap-2">
                  <i className="ri-information-line" aria-hidden="true"></i>
                  回复说明
                </h2>
                <ul className="mt-3 space-y-2.5 text-sm text-secondary-900/85 leading-relaxed">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600 shrink-0" aria-hidden="true"></i>
                    <span>工作时间：{siteInfo.workTime}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600 shrink-0" aria-hidden="true"></i>
                    <span>{siteInfo.replyNote}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600 shrink-0" aria-hidden="true"></i>
                    <span>本站不提供法律代理服务，也不代为处理私人纠纷。</span>
                  </li>
                </ul>
              </div>

              <div className="mt-4 p-5 rounded-lg bg-primary-50 border border-primary-200">
                <h2 className="font-heading text-base font-bold text-primary-900 flex items-center gap-2">
                  <i className="ri-alert-line" aria-hidden="true"></i>
                  正在经历网暴？
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-primary-900/80">
                  如果情况紧急，请优先保存证据并联系平台举报渠道；涉及人身威胁的，请及时报警。写信给我们可能需要等待，不适合作为紧急求助方式。
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 border-t border-background-200 py-12 md:py-16">
        <div className="max-w-content mx-auto px-4 md:px-6">
          <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950">
            投稿前，请先看这四条
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
            这四条不是门槛，而是为了让你的线索更快被用上。很多来信因为缺少基本信息，最终没能进入分析流程。
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {notes.map((item, index) => (
              <div
                key={item.title}
                className="p-5 rounded-lg bg-background-50 border border-background-200"
              >
                <div className="flex items-center justify-between">
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                    <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <span className="font-heading text-xl font-black text-secondary-300">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-4 font-heading text-base md:text-lg font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}